<template>
  <div>
    <h5>Address Details</h5>

    <div class="form-group">
      <div class="col-sm-8">
        <form-label>Your Address</form-label>
        <div class="home-address _margin-bottom-20">
          {{ address.residentialAddresses.present | objectToCsv(['line1', 'line2', 'county', 'city', 'postcode']) }}
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="col-sm-8">
        <form-label id="lengthOfResidencyYears">How long did you live at this address?</form-label>
        <div class="row">
          <div class="col-xs-6 col-sm-6 _margin-bottom-20">
            <validation-messages label="Years" :$v="$v.form.residentialAddresses.years">
              <div class="input-group right">
                <form-field id="lengthOfResidencyYears" v-model="address.residentialAddresses.years"
                  :error="$v.form.residentialAddresses.years.$error" maxlength="3" type="number"
                  @change="onTimeAtAddress" />
                <div class="input-group-addon">years</div>
              </div>
            </validation-messages>
          </div>

          <div class="col-xs-6 col-sm-6 _margin-bottom-20">
            <validation-messages label="Months" :$v="$v.form.residentialAddresses.months">
              <div class="input-group right">
                <form-field id="lengthOfResidencyMonths" v-model="address.residentialAddresses.months"
                  :error="$v.form.residentialAddresses.months.$error" maxlength="2" type="number"
                  @change="onTimeAtAddress" />
                <div class="input-group-addon">months</div>
              </div>
            </validation-messages>
          </div>
        </div>
      </div>
    </div>

    <previous-addressess :$form="$v.form.previousAddresses"
      v-show="(address.residentialAddresses.months || address.residentialAddresses.years) && address.residentialAddresses.present.lengthOfResidencyInMonths < 36" />

    <uiv-alert type="info" v-if="$v.form.residentialAddresses.present.lengthOfResidencyInMonths.maxYears == false">
      Must be less than or equal to 100 years
    </uiv-alert>

    <hr />

    <div class="form-group _margin-bottom-20">
      <div class="col-sm-5">
        <form-label id="residentialStatusId">Current Residential Status</form-label>
        <validation-messages label="Current Residential Status"
          :$v="$v.form.residentialAddresses.present.residentialStatusId">
          <select-list id="residentialStatusId" v-model="address.residentialAddresses.present.residentialStatusId"
            value-key="id" :list="residentialStatusList"
            :error="$v.form.residentialAddresses.present.residentialStatusId.$error" />
        </validation-messages>
      </div>
    </div>

    <div class="form-group _margin-bottom-20">
      <div class="col-sm-6">
        <checkbox id="separateBilling" v-model="businessAddress.separateBilling">
          I have a separate billing address
        </checkbox>
      </div>
    </div>

    <div v-show="businessAddress.separateBilling">
      <div class="form-group" v-show="!businessAddress.manuallyEnterBilling && !businessAddress.text">
        <div class="col-xs-12">
          <validation-messages label="Postcode" :$v="$v.form.businessAddress.postcode">
            <div class="input-group right">
              <form-field id="billingPostcode" type="nospace" placeholder="Search by Postcode"
                v-model="businessAddress.postcode" :error="$v.form.businessAddress.postcode.$error"
                @change="businessAddress.postcode = businessAddress.postcode.toUpperCase()" />
              <div class="input-group-btn">
                <uiv-btn type="primary" :disabled="requesting.billing"
                  @click="onGetBillingAddress(businessAddress.postcode)">
                  <loader :show="requesting.billing" />Search
                </uiv-btn>
              </div>
            </div>
          </validation-messages>
        </div>
      </div>

      <div class="form-group" v-show="billingAddressList.length && !businessAddress.manuallyEnterBilling">
        <div class="col-xs-12">
          <form-label id="billingAddress">
            {{ businessAddress.text ? 'Billing Address' : 'Select Your Billing Address' }}
          </form-label>

          <search-select-list ref="billLookup" id="billingAddress" :select-list="billingAddressList" label-key="text"
            @onChoose="onChooseBillingAddress" :error="$v.form.businessAddress.$error">
            {{ businessAddress.text }}
          </search-select-list>
        </div>
      </div>

      <div v-if="businessAddress.manuallyEnterBilling">
        <div class="form-group _margin-bottom-20">
          <div class="col-sm-7">
            <form-label id="businessLine1">Address Line 1</form-label>
            <validation-messages label="Address Line 1" :$v="$v.form.businessAddress.line1">
              <form-field id="businessLine1" v-model="businessAddress.line1"
                :error="$v.form.businessAddress.line1.$error" />
            </validation-messages>
          </div>
        </div>

        <div class="form-group _margin-bottom-20">
          <div class="col-sm-7">
            <form-label id="businessLine2">Address Line 2</form-label>
            <validation-messages label="Address Line 2" :$v="$v.form.businessAddress.line2">
              <form-field id="businessLine2" v-model="businessAddress.line2"
                :error="$v.form.businessAddress.line2.$error" />
            </validation-messages>
          </div>
        </div>

        <div class="form-group _margin-bottom-20">
          <div class="col-sm-7">
            <form-label id="businessCity">City</form-label>
            <validation-messages label="City" :$v="$v.form.businessAddress.city">
              <form-field id="businessCity" v-model="businessAddress.city"
                :error="$v.form.businessAddress.city.$error" />
            </validation-messages>
          </div>
        </div>

        <div class="form-group _margin-bottom-20">
          <div class="col-sm-7">
            <form-label id="businessCounty">County</form-label>
            <form-field id="businessCounty" v-model="businessAddress.county" />
          </div>
        </div>

        <div class="form-group _margin-bottom-20">
          <div class="col-sm-7">
            <form-label id="businessPostcode">Postcode</form-label>
            <validation-messages label="Postcode" :$v="$v.form.businessAddress.postcode">
              <form-field id="businessPostcode" type="nospace" v-model="businessAddress.postcode"
                :error="$v.form.businessAddress.postcode.$error"
                @change="businessAddress.postcode = businessAddress.postcode.toUpperCase()" />
            </validation-messages>
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="col-sm-4">
          <uiv-btn type="link" size="sm" @click="onReset()"
            v-show="businessAddress.text && !businessAddress.manuallyEnterBilling">
            Search Again
          </uiv-btn>
        </div>

        <div class="text-right" v-show="!businessAddress.text"
          :class="{ 'col-sm-3': businessAddress.manuallyEnterBilling, 'col-sm-4': !businessAddress.manuallyEnterBilling }">
          <uiv-btn type="link" size="sm" @click="onCantFindAddress()">
            {{ !businessAddress.manuallyEnterBilling ? 'Can\'t find your address?' : 'Search By Postcode' }}
          </uiv-btn>
        </div>
      </div>
    </div>

    <div class="sticky-footer">
      <div class="col-xs-5">
        <uiv-btn type="link" block @click="goToQuestion('details')">Back</uiv-btn>
      </div>
      <div class="col-xs-7">
        <uiv-btn type="primary" block @click="onNext">Next</uiv-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {
  required, helpers, requiredIf, maxLength, numeric
} from 'vuelidate/lib/validators';
import { mapState, mapActions } from 'vuex';
import PreviousAddressess from './PreviousAddressess';

export default {
  name: 'CustomerAddress',
  data() {
    return {
      billingAddressList: [],
      requesting: {
        billing: false
      },
      form: {
        previousAddresses: [],
        address: {
          residentialAddresses: {
            present: {}
          }
        }
      }
    }
  },
  mounted() {
    this.form = this.address;
  },
  validations() {
    return {
      form: {
        previousAddresses: {
          atLeast3Years(value) {
            const { lengthOfResidencyInMonths } = this.address.residentialAddresses.present;
            const totalMonths = this.address.previousAddresses.reduce((acc, item) => {
              const months = item.lengthOfResidencyInMonths || 0;
              return acc + months;
            }, 0);

            return totalMonths + (lengthOfResidencyInMonths || 0) >= 36;
          },
          $each: {
            lengthOfResidencyInMonths: { required, numeric },
            line1: { requiredIf: requiredIf((form) => form.manually) },
            line2: { requiredIf: requiredIf((form) => form.manually) },
            city: { requiredIf: requiredIf((form) => form.manually) },
            postcode: {
              required,
              maxLength: maxLength(7),
              regex: helpers.regex('postcode', /^(([gG][iI][rR] {0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?[0-9][0-9]?)|(([a-pr-uwyzA-PR-UWYZ][0-9][a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y][0-9][abehmnprv-yABEHMNPRV-Y]))) {0,}[0-9][abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/)
            }
          }
        },
        residentialAddresses: {
          years: {
            numeric,
            requiredIf: requiredIf((form) => {
              const { years, months } = this.address.residentialAddresses;
              return !months && !years;
            })
          },
          months: {
            numeric,
            requiredIf: requiredIf((form) => {
              const { years, months } = this.address.residentialAddresses;
              return !months && !years;
            })
          },
          present: {
            residentialStatusId: { required },
            lengthOfResidencyInMonths: {
              required, numeric,
              maxYears(value) {
                const { lengthOfResidencyInMonths } = this.address.residentialAddresses.present;

                const totalMonths = this.address.previousAddresses.reduce((acc, item) => {
                  const months = item.lengthOfResidencyInMonths || 0;
                  return acc + months;
                }, 0);

                return totalMonths + (lengthOfResidencyInMonths || 0) <= 1200;
              }
            }
          }
        },
        businessAddress: {
          line1: { requiredIf: requiredIf(() => this.address.businessAddress.separateBilling) },
          line2: { requiredIf: requiredIf(() => this.address.businessAddress.separateBilling) },
          city: { requiredIf: requiredIf(() => this.address.businessAddress.separateBilling) },
          postcode: {
            maxLength: maxLength(7),
            requiredIf: requiredIf(() => this.address.businessAddress.separateBilling),
            regex: helpers.regex('postcode', /^(([gG][iI][rR] {0,}0[aA]{2})|((([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y]?[0-9][0-9]?)|(([a-pr-uwyzA-PR-UWYZ][0-9][a-hjkstuwA-HJKSTUW])|([a-pr-uwyzA-PR-UWYZ][a-hk-yA-HK-Y][0-9][abehmnprv-yABEHMNPRV-Y]))) {0,}[0-9][abd-hjlnp-uw-zABD-HJLNP-UW-Z]{2}))$/)
          }
        }
      }
    }
  },
  computed: {
    ...mapState({
      appSettings: ({ app_settings }) => app_settings,
      switchPlan: ({ personal_details_switch_plan }) => personal_details_switch_plan,
      quote: ({ storage }) => storage.quote,
      address: ({ personal_details_address }) => personal_details_address,
      businessAddress: ({ personal_details_address }) => personal_details_address.businessAddress,
      residentialStatusList: ({ lookups }) => lookups.residentialStatuses
    })
  },
  methods: {
    ...mapActions({
      findAddresses: 'lookups/getAddresses',
      getXoserveMeters: 'lookups/getXoserveMeters',
      resetBillingAddress: 'personal_details_address/resetBillingAddress',
      goToQuestion: 'personal_details_questions/goToQuestion'
    }),
    onReset() {
      this.resetBillingAddress();
      this.$refs.billLookup.onReset();
    },
    async onGetBillingAddress(postcode) {
      this.requesting.billing = true;
      this.billingAddressList = [];
      this.$v.form.businessAddress.postcode.$touch();
      if (this.$v.form.businessAddress.postcode.$invalid) return;

      this.billingAddressList = await this.findAddresses(postcode);
      this.requesting.billing = false;
    },
    onChooseBillingAddress(selected) {
      this.businessAddress.line1 = selected.addressLine1;
      this.businessAddress.line2 = selected.addressLine2;
      this.businessAddress.city = selected.city;
      this.businessAddress.county = selected.county;
      this.businessAddress.postcode = selected.postcode.replace(' ', '');
      this.businessAddress.text = selected.text;
      this.$forceUpdate();
    },
    onTimeAtAddress() {
      const { residentialAddresses } = this.address
      const years = (y) => parseInt(y || 0);
      const months = (m) => parseInt(m || 0);
      const total = (years(residentialAddresses.years) * 12) + months(residentialAddresses.months);

      this.address.residentialAddresses.years = residentialAddresses.years == 0 ? null : residentialAddresses.years;
      this.address.residentialAddresses.months = residentialAddresses.months == 0 ? null : residentialAddresses.months;
      this.address.residentialAddresses.present.lengthOfResidencyInMonths = total;
    },
    onCantFindAddress() {
      this.resetBillingAddress();
      this.$refs.billLookup.onReset();
      this.businessAddress.manuallyEnterBilling = !this.businessAddress.manuallyEnterBilling;
      this.$v.form.businessAddress.postcode.$reset();
    },
    onNext() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      this.$scrollTo('.fullscreen-card');
      this.goToQuestion('terms');
    }
  },
  components: {
    PreviousAddressess
  }
};
</script>

<style lang="scss" scoped>
.home-address {
  background-color: #EEEEEE;
  padding: 15px;
  border-radius: 4px;
  font-size: 15px;
  min-height: 51px;
}
</style>
